/** 9.5 Proyecciones de ventas y cobros */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { TrendUpIcon, ChartLineUpIcon, TargetIcon } from "@phosphor-icons/react";

const DIMS = [
  { k: "", l: "General" },
  { k: "region", l: "Por región" },
  { k: "rtc", l: "Por RTC" },
  { k: "linea", l: "Por línea / proveedor" },
  { k: "cliente", l: "Por cliente" },
];

export default function ProjectionsReports() {
  const [proy, setProy] = useState(null);
  const [cobros, setCobros] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({ dim: "", meses_historia: 6, meses_proyeccion: 3, pct_optimista: 15, pct_conservador: 15, horizonte: 60 });
  const [gen, setGen] = useState({ dimension: "region", escenario: "base" });
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/reports/projections/sales`, { params: {
          meses_historia: cfg.meses_historia, meses_proyeccion: cfg.meses_proyeccion,
          pct_optimista: cfg.pct_optimista, pct_conservador: cfg.pct_conservador,
          ...(cfg.dim ? { dimension: cfg.dim } : {}),
        } }),
        api.get(`/reports/projections/cobros-esperados`, { params: { proximos_dias: cfg.horizonte } }),
      ]);
      setProy(a.data); setCobros(b.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cfg]);

  const genPayload = (extra = {}) => ({
    dimension: gen.dimension, escenario: gen.escenario,
    meses_historia: cfg.meses_historia, meses_proyeccion: cfg.meses_proyeccion,
    pct_optimista: cfg.pct_optimista, pct_conservador: cfg.pct_conservador, ...extra,
  });

  const previewMetas = async () => {
    try {
      const { data } = await api.post(`/reports/projections/generar-metas`, genPayload({ dry_run: true }));
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const generarMetas = async (sobrescribir = false) => {
    if (sobrescribir && !window.confirm("Se sobrescribirán metas existentes con los montos propuestos (quedarán como borrador). ¿Continuar?")) return;
    try {
      const { data } = await api.post(`/reports/projections/generar-metas`, genPayload({ sobrescribir }));
      toast.success(`Metas propuestas: ${data.creadas.length} creadas, ${data.actualizadas.length} actualizadas${data.conflictos.length ? `, ${data.conflictos.length} existentes sin tocar` : ""}`);
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const aprobar = async () => {
    const ids = [...(preview?.creadas || []), ...(preview?.actualizadas || [])].map((x) => x.id).filter(Boolean);
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/reports/projections/metas-propuestas/aprobar`, { ids });
      toast.success(`${data.aprobadas} metas aprobadas`);
      setPreview(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setC = (patch) => setCfg({ ...cfg, ...patch });

  return (
    <div className="space-y-5">
      {/* Configuración */}
      <div className="bg-card border border-border/60 rounded-lg p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <Field label="Dimensión">
          <select value={cfg.dim} onChange={(e) => setC({ dim: e.target.value })} data-testid="proy-dim" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            {DIMS.map((d) => <option key={d.k} value={d.k}>{d.l}</option>)}
          </select>
        </Field>
        <Field label="Meses historia">
          <NumInput value={cfg.meses_historia} onCommit={(v) => setC({ meses_historia: v })} min={2} max={36} />
        </Field>
        <Field label="Meses a proyectar">
          <NumInput value={cfg.meses_proyeccion} onCommit={(v) => setC({ meses_proyeccion: v })} min={1} max={24} />
        </Field>
        <Field label="% optimista">
          <NumInput value={cfg.pct_optimista} onCommit={(v) => setC({ pct_optimista: v })} min={0} max={500} testid="proy-pct-opt" />
        </Field>
        <Field label="% conservador">
          <NumInput value={cfg.pct_conservador} onCommit={(v) => setC({ pct_conservador: v })} min={0} max={100} testid="proy-pct-cons" />
        </Field>
        <Field label="Horizonte cobros (días)">
          <select value={cfg.horizonte} onChange={(e) => setC({ horizonte: Number(e.target.value) })} data-testid="proy-horizonte" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
            {[15, 30, 60, 90, 120, 180, 365].map((d) => <option key={d} value={d}>{d} días</option>)}
          </select>
        </Field>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60">
          <h3 className="font-heading text-base flex items-center gap-2"><TrendUpIcon size={16} /> Proyección de ventas ({cfg.meses_proyeccion} meses){loading ? " · cargando…" : ""}</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Histórico</div>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border"><th className="p-2">Período</th><th className="p-2 text-right">Total</th></tr></thead>
                <tbody>
                  {(proy?.historico || []).map((h, i) => (
                    <tr key={i} className="border-b border-border/30">
                      <td className="p-2">{h.periodo}</td>
                      <td className="p-2 text-right font-medium">{fmtL(h.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 text-[10px] text-text-muted">Pendiente lineal: {proy?.slope_mensual >= 0 ? "+" : ""}{fmtL(proy?.slope_mensual || 0)}/mes</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Proyección (3 escenarios) vs meta</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
                    <th className="p-2">Período</th>
                    <th className="p-2 text-right text-red-400">Conserv. (−{cfg.pct_conservador}%)</th>
                    <th className="p-2 text-right text-agro-yellow">Base</th>
                    <th className="p-2 text-right text-agro-green">Optim. (+{cfg.pct_optimista}%)</th>
                    <th className="p-2 text-right">Meta</th>
                    <th className="p-2 text-right">Base vs meta</th>
                  </tr>
                </thead>
                <tbody>
                  {(proy?.proyeccion || []).map((p, i) => (
                    <tr key={i} className="border-b border-border/30">
                      <td className="p-2">{p.periodo}</td>
                      <td className="p-2 text-right text-red-400">{fmtL(p.proyectado_conservador)}</td>
                      <td className="p-2 text-right text-agro-yellow font-medium">{fmtL(p.proyectado_base)}</td>
                      <td className="p-2 text-right text-agro-green">{fmtL(p.proyectado_optimista)}</td>
                      <td className="p-2 text-right text-text-muted">{p.meta ? fmtL(p.meta) : "—"}</td>
                      <td className="p-2 text-right">{p.vs_meta_pct != null ? `${p.vs_meta_pct}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 text-[10px] text-text-muted">Base = (tendencia + mismo mes año anterior) / 2. Incluye ventas de contado.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Proyección por dimensión */}
      {proy?.por_dimension && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="proy-por-dim">
          <div className="p-4 border-b border-border/60">
            <h3 className="font-heading text-sm">Proyección por {DIMS.find((d) => d.k === cfg.dim)?.l.replace("Por ", "")}</h3>
          </div>
          <div className="overflow-x-auto max-h-[480px]">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">Nombre</th>
                  <th className="p-2 text-right">Histórico ({cfg.meses_historia}m)</th>
                  {(proy.proyeccion || []).map((p) => <th key={p.periodo} className="p-2 text-right">{p.periodo}</th>)}
                  <th className="p-2 text-right">Total proyectado</th>
                  <th className="p-2 text-right">Meta</th>
                  <th className="p-2 text-right">vs meta</th>
                </tr>
              </thead>
              <tbody>
                {proy.por_dimension.length === 0 ? <tr><td colSpan="10" className="p-6 text-center text-text-muted">Sin datos</td></tr>
                : proy.por_dimension.map((d, i) => (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-2">{d.nombre}</td>
                    <td className="p-2 text-right text-text-muted">{fmtL(d.total_historico)}</td>
                    {(proy.proyeccion || []).map((p) => {
                      const x = d.proyeccion.find((q) => q.periodo === p.periodo);
                      return <td key={p.periodo} className="p-2 text-right">{x ? fmtL(x.proyectado_base) : "—"}</td>;
                    })}
                    <td className="p-2 text-right font-medium text-agro-yellow">{fmtL(d.total_proyectado)}</td>
                    <td className="p-2 text-right text-text-muted">{d.total_meta ? fmtL(d.total_meta) : "—"}</td>
                    <td className={`p-2 text-right ${d.total_meta ? (d.total_proyectado >= d.total_meta ? "text-agro-green" : "text-red-400") : "text-text-muted"}`}>
                      {d.total_meta ? `${((d.total_proyectado / d.total_meta) * 100).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Generar metas propuestas */}
      <div className="bg-card border border-border/60 rounded-lg p-4" data-testid="proy-generar-metas">
        <div className="flex flex-wrap items-end gap-3">
          <h3 className="font-heading text-sm flex items-center gap-2 mr-auto"><TargetIcon size={16} /> Generar metas propuestas</h3>
          <Field label="Para">
            <select value={gen.dimension} onChange={(e) => setGen({ ...gen, dimension: e.target.value })} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="total">Meta global</option><option value="region">Por región</option><option value="rtc">Por RTC</option>
            </select>
          </Field>
          <Field label="Escenario">
            <select value={gen.escenario} onChange={(e) => setGen({ ...gen, escenario: e.target.value })} className="bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="conservador">Conservador</option><option value="base">Base</option><option value="optimista">Optimista</option>
            </select>
          </Field>
          <button onClick={previewMetas} className="px-3 py-1.5 text-xs border border-border rounded-md hover:border-agro-green" data-testid="proy-preview-metas">Previsualizar</button>
          <button onClick={() => generarMetas(false)} className="px-3 py-1.5 text-xs bg-agro-green text-black font-medium rounded-md" data-testid="proy-crear-metas">Crear borradores</button>
        </div>
        {preview && (
          <div className="mt-3 text-xs space-y-2">
            <div className="text-text-secondary">
              {preview.dry_run ? "Vista previa: " : ""}{preview.creadas.length} nuevas · {preview.actualizadas.length} actualizadas · {preview.conflictos.length} ya existen
            </div>
            {preview.conflictos.length > 0 && (
              <div className="p-2 border border-agro-yellow/30 bg-agro-yellow/5 rounded">
                Existen metas para {preview.conflictos.length} combinaciones; no se modificaron.
                <button onClick={() => generarMetas(true)} className="ml-2 underline text-agro-yellow" data-testid="proy-sobrescribir">Sobrescribir (requiere confirmación)</button>
              </div>
            )}
            {!preview.dry_run && (preview.creadas.length + preview.actualizadas.length) > 0 && (
              <button onClick={aprobar} className="px-3 py-1.5 text-xs border border-agro-green text-agro-green rounded-md" data-testid="proy-aprobar-metas">Aprobar estas metas</button>
            )}
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full">
                <tbody>
                  {[...preview.creadas, ...preview.actualizadas, ...preview.conflictos].slice(0, 60).map((m, i) => (
                    <tr key={i} className="border-t border-border/30">
                      <td className="p-1">{m.year}-{String(m.month).padStart(2, "0")}</td>
                      <td className="p-1">{m.region || m.rtc_nombre || "Global"}</td>
                      <td className="p-1 text-right">{fmtL(m.monto)}</td>
                      <td className="p-1 text-right text-text-muted">{m.monto_actual != null ? `actual ${fmtL(m.monto_actual)}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60">
          <h3 className="font-heading text-base flex items-center gap-2"><ChartLineUpIcon size={16} /> Cobros esperados (próximos {cfg.horizonte} días)</h3>
          <div className="text-[10px] text-text-muted mt-1">Total esperado: <strong className="text-agro-green">{fmtL(cobros?.total || 0)}</strong></div>
        </div>
        <div className="overflow-x-auto max-h-80">
          <table className="w-full text-xs">
            <thead className="bg-sidebar/60 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-2">Período</th>
                <th className="p-2 text-right">Monto esperado</th>
                <th className="p-2 text-right"># Facturas</th>
                <th className="p-2 text-right">Vencidas</th>
              </tr>
            </thead>
            <tbody>
              {(cobros?.rows || []).map((r, i) => (
                <tr key={i} className={`border-t border-border/30 ${r.periodo === "Vencidas" ? "bg-red-500/5" : ""}`}>
                  <td className={`p-2 ${r.periodo === "Vencidas" ? "text-red-400 font-medium" : ""}`}>{r.periodo}</td>
                  <td className="p-2 text-right font-medium">{fmtL(r.monto_esperado)}</td>
                  <td className="p-2 text-right">{r.facturas}</td>
                  <td className="p-2 text-right text-red-400">{r.vencidas_incluidas || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{label}</label>
      {children}
    </div>
  );
}

/** Input numérico que aplica el valor al salir del campo o con Enter (evita recargas por tecla). */
function NumInput({ value, onCommit, min, max, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const commit = () => {
    const n = Math.max(min, Math.min(max, Number(v) || 0));
    setV(n);
    if (n !== value) onCommit(n);
  };
  return (
    <input type="number" min={min} max={max} value={v} data-testid={testid}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
  );
}
